import { HtmlParser } from '@calendly/html-parser';
import { I18n } from '@calendly/i18n';
import { colorTextAction } from '@calendly/ui/theme';
import classNames from 'classnames';
import { isEmpty, pick } from 'lodash-es';
import moment from 'moment-timezone';
import { forwardRef } from 'react';
import { convertHostIdsToMeetingRoles } from 'components/meetings/filter_modal/utils';
import { useScope } from 'hooks/meetings/use_scope';
import { STATUS_MAP, Statuses } from '../../../services/search_api/constants';
import { useLookupUuidsQuery } from '../api';
import { ANALYTICS_SEARCH_IN, MATCH_ATTENDEE, MATCH_MEETING, MATCH_TRANSCRIPT, MATCH_TYPE_MAPPING, SEARCH_IN } from './constants';
const highlightSpanCss = "h195mlbx";
const HighlightSpan = forwardRef((props, ref) => {
  const {
    as: AsComponent,
    ...restProps
  } = props;
  const Component = AsComponent || 'span';
  return <Component ref={ref} {...restProps} className={classNames(highlightSpanCss, props.className)} />;
});
const formatTime = (dateTime, timezone, timeNotation, includeMeridiem = true) => {
  const time = moment.tz(dateTime, timezone);
  if (timeNotation === '24h') {
    return time.format('H:mm');
  }
  let timeFormat = time.minutes() !== 0 ? 'h:mm' : 'h';
  if (includeMeridiem) {
    timeFormat += ' a';
  }
  return time.format(timeFormat);
};
export const formatTimeRange = (startTime, endTime, timezone, timeNotation) => {
  const isSameMeridiem = moment.tz(startTime, timezone).format('a') === moment.tz(endTime, timezone).format('a');
  const startFormat = formatTime(startTime, timezone, timeNotation, !isSameMeridiem);
  const endFormat = formatTime(endTime, timezone, timeNotation);
  return `${startFormat} - ${endFormat}`;
};
export const formatDateRelative = (dateTime, timezone) => moment.tz(dateTime, timezone).calendar({
  sameDay: `[${I18n.t('publisher.meetings.search.date_format.today')}]`,
  nextDay: `[${I18n.t('publisher.meetings.search.date_format.tomorrow')}]`,
  nextWeek: 'dddd',
  lastDay: `[${I18n.t('publisher.meetings.search.date_format.yesterday')}]`,
  lastWeek: 'MMMM D',
  sameElse(now) {
    return this.isSame(now, 'year') ? 'MMMM D' : 'MMMM D, YYYY';
  }
});
export const convertSearchResponse = (response, limit = null) => {
  if (!response) return null;
  const {
    meetings_search: meetings
  } = response;
  const meetingEdges = limit ? meetings.edges.slice(0, limit) : meetings.edges;
  const data = meetingEdges.map(({
    node
  }) => ({
    ...node,
    color: node.eventType?.color || colorTextAction,
    attendees: (node.attendees || []).filter(({
      kind
    }) => kind === 'INVITEE'),
    guests: (node.attendees || []).filter(({
      kind
    }) => kind === 'EXTERNAL'),
    transcriptMatches: [],
    hasMeetingIntelligence: Boolean(node.meeting_intelligence),
    trackedMeetingUuid: node.meeting_intelligence?.uuid
  }));
  const results = {
    totalSize: meetings.total,
    data
  };
  return results;
};
const dateRangeFilter = params => {
  switch (params.period) {
    case 'fixed':
      return {
        operation: 'BETWEEN',
        start: moment(params.start).startOf('day').toISOString(),
        end: moment(params.end).endOf('day').toISOString()
      };
    case 'today':
      return {
        operation: 'BETWEEN',
        start: moment().startOf('day').toISOString(),
        end: moment().endOf('day').toISOString()
      };
    case 'this_week':
      return {
        operation: 'BETWEEN',
        start: moment().startOf('week').toISOString(),
        end: moment().endOf('week').toISOString()
      };
    case 'this_month':
      return {
        operation: 'BETWEEN',
        start: moment().startOf('month').toISOString(),
        end: moment().endOf('month').toISOString()
      };
    case 'this_year':
      return {
        operation: 'BETWEEN',
        start: moment().startOf('year').toISOString(),
        end: moment().endOf('year').toISOString()
      };
    default:
      return null;
  }
};
const useFilterParamsWithUuids = filterParams => {
  const {
    data: uuids
  } = useLookupUuidsQuery({
    filters: filterParams
  });
  if (!uuids) {
    return filterParams;
  }
  return {
    ...filterParams,
    ...uuids
  };
};
const buildStatuses = ({
  status
}) => {
  if (!status || status.length === 0 || status.includes(Statuses.ALL)) return null;
  const statusValues = status.map(s => STATUS_MAP[s]).filter(Boolean);
  return statusValues.length > 0 ? statusValues : null;
};
export const useToPrepareSearchFiltersParams = filters => {
  const {
    scopeType,
    scopeId
  } = useScope();
  const hydratedFilters = useFilterParamsWithUuids(filters);
  const {
    dateRange,
    ...restFilters
  } = hydratedFilters;
  if (isEmpty(restFilters.searchIn)) {
    restFilters.searchIn = [MATCH_ATTENDEE, MATCH_TRANSCRIPT, MATCH_MEETING];
  }

  // Use the shared conversion function to map host_ids to MeetingRoles
  const mappedRoles = convertHostIdsToMeetingRoles(restFilters.role, scopeType, scopeId);
  const kinds = restFilters.schedulingMethod?.map(method => method.toUpperCase()) || [];
  const statuses = buildStatuses(restFilters);
  return {
    ...pick(restFilters, ['searchIn', 'inviteeEmails']),
    ...(kinds.length > 0 && {
      kinds
    }),
    ...(statuses && {
      statuses
    }),
    ...(mappedRoles && mappedRoles.length > 0 && {
      role: mappedRoles
    }),
    startTimeRange: dateRangeFilter(dateRange),
    eventTypeUuids: restFilters.eventTypeIds,
    packageUuids: restFilters.packageUuids
  };
};
export const getMatchTypeFilters = filterParams => {
  const {
    searchIn = []
  } = filterParams;
  if (isEmpty(searchIn) || searchIn.length === SEARCH_IN.length) return ANALYTICS_SEARCH_IN;
  return searchIn.filter(type => type in MATCH_TYPE_MAPPING).map(type => MATCH_TYPE_MAPPING[type]);
};
const EmphasisComponent = ({
  children
}) => <HighlightSpan>{children}</HighlightSpan>;
export const HighlightedText = ({
  html
}) => {
  if (!html) return null;
  return HtmlParser(html, {
    em: EmphasisComponent
  });
};
export const getAttendeeIdentifier = ({
  name,
  email
}) => name || email;

require("./utils.wyw-in-js.css!=!../../../../../node_modules/@wyw-in-js/webpack-loader/lib/outputCssLoader.js?cacheProvider=!./utils.js");